<template>
  <div>
    <CModal
      :show.sync="show"
      @close="close"
      centered
      size="lg"
    >
      <template #header>
        <div>
          <div class="d-flex align-items-center">
            <CIcon name="cipBriefcase01" size="custom-size" width="36" height="36"
                   class="mr-3 icon-circle icon-circle--primary"/>
            <div>
              <h5 class="modal-title">
                <translate translate-context="payout.companyManagement">Company management</translate>
              </h5>
            </div>
          </div>
        </div>
        <CButtonClose @click="close"/>
      </template>
      <template #default>lll
        <!-- Loading -->
        <div v-if="isLoading" class="text-center py-4">
          <CSpinner color="primary"/>
        </div>

        <!-- CM Selector (multi-CM, no preselection) -->
        <div v-else-if="showCmSelector" class="py-2">
          <p class="mb-3">
            <translate translate-context="payout.companyManagement">Select the company to manage:</translate>
          </p>
          <ContractManagerSelector
            :contract-managers="contractManagers"
            :selected-id="selectedCmId"
            @select="onSelectCm"
          />
        </div>

        <!-- Read-only view for certified -->
        <div v-else-if="showCertifiedView">
          <CCard class="mb-3">
            <CCardBody>
              <div class="font-weight-bold mb-2">
                <translate translate-context="payout.companyManagement">Company data</translate>
              </div>
              <div class="mb-1" v-if="certification.vat_number">
                <small class="text-muted">
                  <translate translate-context="payout.companyManagement">VAT</translate>
                </small>
                <div>{{ certification.vat_number }}</div>
              </div>
              <div class="mb-1" v-if="certification.region">
                <small class="text-muted">
                  <translate translate-context="payout.companyManagement">Region</translate>
                </small>
                <div>{{ certification.region === 'eu' ? 'EU' : 'Extra EU' }}</div>
              </div>
              <CBadge color="success">
                <translate translate-context="payout.companyManagement">Approved</translate>
              </CBadge>
            </CCardBody>
          </CCard>

          <!-- IBAN field (only if user has update_iban permission) -->
          <div v-if="canUpdateIban" class="form-group">
            <label class="font-weight-bold">IBAN</label>
            <CInput
              v-model="ibanValue"
              type="text"
              maxlength="34"
              :placeholder="'IT60X0542811101000000123456'"
            />
          </div>
          <div v-else-if="certification.iban" class="form-group">
            <label class="font-weight-bold">IBAN</label>
            <div>{{ certification.iban }}</div>
          </div>
        </div>

        <!-- No CMs found -->
        <div v-else-if="noCMs" class="text-center py-4">
          <p class="text-muted">
            <translate translate-context="payout.companyManagement">No companies found.</translate>
          </p>
        </div>
      </template>

      <template #footer>
        <CButton color="secondary" variant="outline" @click="close">
          <translate translate-context="payout.companyManagement">Close</translate>
        </CButton>
        <CButton
          v-if="showCertifiedView && canUpdateIban"
          color="primary"
          @click="handleSaveIban"
          :disabled="isSaving"
        >
          <CSpinner v-if="isSaving" size="sm" class="mr-2"/>
          <translate translate-context="payout.companyManagement">Save</translate>
        </CButton>
      </template>
    </CModal>

    <CertificationModal
      ref="certificationModal"
      :is-submitting="isSaving"
      :can-submit="canSubmit"
      :can-validate-vat="canValidateVat"
      @submit="handleCertificationSubmit"
    />
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import ContractManagerSelector from './ContractManagerSelector.vue'
import CertificationModal from './CertificationModal.vue'
import {CERTIFICATION_STATUS} from '../constants'
import eventBus from '@/domain/core/constant/eventBus'

export default {
  name: 'CompanyManagementModal',
  components: {
    ContractManagerSelector,
    CertificationModal
  },
  data() {
    return {
      show: false,
      isLoading: false,
      isSaving: false,
      ibanValue: '',
      selectedCmId: null
    }
  },
  computed: {
    ...mapGetters('payout', [
      'contractManagers',
      'certification',
      'certificationStatus'
    ]),
    showCmSelector() {
      return this.contractManagers.length > 1 && !this.selectedCmId
    },
    showCertifiedView() {
      return this.selectedCmId && this.certification.status === CERTIFICATION_STATUS.CERTIFIED
    },
    noCMs() {
      return this.contractManagers.length === 0
    },
    canSubmit() {
      return this.hasPerm('contract_manager.accounting.certification.submit')
    },
    canValidateVat() {
      return this.hasPerm('contract_manager.accounting.certification.validate_vat')
    },
    canUpdateIban() {
      return this.hasPerm('contract_manager.accounting.certification.update_iban')
    }
  },
  methods: {
    ...mapActions('payout', [
      'setSelectedCmId',
      'fetchCertificationScopes',
      'fetchCertification',
      'submitCertification',
      'updateCertification',
      'updateCertificationIban'
    ]),
    ...mapActions('login', ['fetchConfig']),

    /**
     * Apri la modale.
     * @param {number|null} cmId - Se passato, skippa il discovery e usa direttamente questo CM
     */
    async open(cmId = null) {
      this.selectedCmId = null
      this.ibanValue = ''

      if (cmId) {
        // Chiamato dal payout con CM già noto — apri direttamente
        await this.onSelectCm(cmId)
      } else {
        // Chiamato dall'header — discovery CM
        this.show = true
        this.isLoading = true

        try {
          const contractManagers = await this.fetchCertificationScopes()

          if (contractManagers.length === 1) {
            await this.onSelectCm(contractManagers[0].id)
          }
        } catch (e) {
          this.notifyError(e)
        } finally {
          this.isLoading = false
        }
      }
    },

    close() {
      this.show = false
    },

    async onSelectCm(cmId) {
      this.selectedCmId = cmId
      this.isLoading = true

      try {
        this.setSelectedCmId(cmId)

        // Fetch config permessi e certificazione in parallelo
        await Promise.all([
          this.fetchConfig({type: 'certification_contract_manager', id: cmId}),
          this.fetchCertification()
        ])

        const status = this.certification.status

        if (status === CERTIFICATION_STATUS.CERTIFIED) {
          this.ibanValue = this.certification.iban || ''
          this.show = true
          this.isLoading = false
        } else {
          this.isLoading = false
          this.show = false
          this.$nextTick(() => {
            this.$refs.certificationModal.open(
              status !== CERTIFICATION_STATUS.NOT_CERTIFIED ? this.certification : null
            )
          })
        }
      } catch (e) {
        this.isLoading = false
        this.notifyError(e)
      }
    },

    async handleSaveIban() {
      if (!this.selectedCmId) return

      this.isSaving = true
      try {
        await this.updateCertificationIban(this.ibanValue.replace(/\s/g, '').toUpperCase())
        this.notifySuccess(this.$pgettext('payout.companyManagement', 'IBAN updated successfully'))
        this.close()
        this.$emit('updated')
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.isSaving = false
      }
    },

    async handleCertificationSubmit(formData) {
      if (!this.selectedCmId) return

      this.isSaving = true
      try {
        if (this.certificationStatus === CERTIFICATION_STATUS.NOT_CERTIFIED) {
          await this.submitCertification(formData)
        } else {
          await this.updateCertification(formData)
        }

        this.$refs.certificationModal.close()
        this.notifySuccess(this.$pgettext('payout.companyManagement', 'Certification submitted successfully'))
        this.$emit('updated')
        this.emitEvent(eventBus.certification.submitted)
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>
